import {
  DataType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button, Grid } from '@material-ui/core'
import { Sync } from '@material-ui/icons'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  EnumCommodity,
  EnumEstadoCiclo,
  EnumPeriodicityType,
  EnumTipoFacturacion,
  EnumTipoPeriodicidad
} from '../../api'
import {
  cfExecutionRequestModel,
  InvoiceCycleWithLastExecutionModel
} from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import {
  CalculateNextExecutionRequest,
  CreateInvoiceCycleRequest,
  UpdateInvoiceCycleRequest
} from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/createInvoiceCycleRequest'
import { InvoiceCycleCompatibilities } from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/invoiceCycleCompatibilities'
import { EnumClientTypes } from '../../api/Argentia/enums/enumClientTypes'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import { ExecutionCycleFormDialog } from '../../components/shared/executionCycleFormDialog'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import {
  enumToMapValues,
  enumToSelectOptionArray,
  enumTranslateOption,
  enumValueToKey,
  MapToArray
} from '../../helpers'
import { hasPermission, hasResource } from '../../helpers/permissionsHelper'
import { FormDialogDefinitionProps, usePageState } from '../../hooks'
import { useFormDialogV2 } from '../../hooks/useFormDialogV2'
import { NonAuthRoutes } from '../../model'
import { Resources } from '../../model/RBAC/constants'
import {
  getActiveCommodities,
  getActiveCommodity,
  storeActiveCommodity,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage
} from '../../redux'
import { AllCyclesTable } from './components/allCyclesTable/allCyclesTable'
import {
  FormSelections,
  getFilteredCompatibilityOptions,
  mapCompatibilitiesToSelectOptions
} from './invoiceCycles.mapper'
import {
  calculateNextExecution,
  createInvoiceCycle,
  createInvoiceCycleExecution,
  editInvoiceCycle,
  fetchAvailableInvoiceCycles,
  fetchInvoiceCycleCompatibilities
} from './invoiceCycles.service'

export interface InvoiceCyclesPageProps {}

export const InvoiceCyclesNewPage: React.FC<InvoiceCyclesPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'invoiceCyclesPage'])
  const [showAllCycles, setShowAllCycles] = useState<boolean>(false)
  const { dataTableFilter, dataTableSearch, activeTab } = usePageState()

  const commodities = getActiveCommodities()
  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (commodities !== undefined && commodities?.length > 0
      ? enumValueToKey(EnumCommodity, commodities[0])
      : EnumCommodity.GasNatural)
  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)
  const [currentClientTypeTab, setCurrentClientTypeTab] = useState<number>(activeTab ?? 0)

  const { info, error } = useNotify()
  const history = useHistory()
  const {
    openForm: openFormV2,
    closeForm: closeFormV2,
    formProps: formPropsV2,
    CustomFormDialog: CustomFormDialogV2,
    updateFormData: updateFormDataV2,
    updateCurrentDefinition
  } = useFormDialogV2()
  const [isFetchingAvailableCycles, setIsFetchingAvailableCycles] = useState<boolean>(false)
  const [availableCycles, setAvailableCycles] = useState<
    (SelectOption & { invoiceType: number; invoiceCycleSubtype: number | undefined })[]
  >([])

  // Invoice cycle compatibilities state
  const [isLoadingCompatibilities, setIsLoadingCompatibilities] = useState<boolean>(false)
  const [compatibilitiesData, setCompatibilitiesData] = useState<InvoiceCycleCompatibilities[]>([])
  const [allCompatibilityOptions, setAllCompatibilityOptions] = useState<
    ReturnType<typeof mapCompatibilitiesToSelectOptions>
  >({
    subjectTypes: [],
    invoiceTypes: [],
    cycles: [],
    subtypes: []
  })
  const [currentFormSelections, setCurrentFormSelections] = useState<FormSelections>({})
  const [subjectTypeOptions, setSubjectTypeOptions] = useState<SelectOption[]>([])
  const [invoiceTypeOptions, setInvoiceTypeOptions] = useState<SelectOption[]>([])
  const [cycleOptions, setCycleOptions] = useState<SelectOption[]>([])
  const [subtypeOptions, setSubtypeOptions] = useState<SelectOption[]>([])
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0) // Trigger to force child components refresh
  const [showStartExecutionForm, setShowStartExecutionForm] = useState<boolean>(false)
  const [executionFormInitialData, setExecutionFormInitialData] = useState<
    | {
        cycleSelect?: number
        cycleName?: string
      }
    | undefined
  >(undefined)

  const [selectedPeriodicityType, setSelectedPeriodicityType] = useState<number | undefined>(
    EnumTipoPeriodicidad.Variable
  )
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<number | undefined>(undefined)

  const hasClientOrExtendedEditReadOnlyPermission =
    hasPermission(Resources.Client, [UserPermission.EDIT, UserPermission.READ_ONLY]) ||
    hasPermission(Resources.ExtendedClients, [UserPermission.EDIT, UserPermission.READ_ONLY])

  const handleShowAllCycles = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setShowAllCycles(checked)
  }

  const ClientTabsArray = MapToArray(enumToMapValues(EnumClientTypes))

  const filteredTabs = ClientTabsArray.filter(tab => {
    if (tab.value === EnumClientTypes.FINAL_CLIENT) {
      return hasResource(Resources.ExtendedClients) ? false : true
    } else if (tab.value === EnumClientTypes.EXTENDED_CLIENT) {
      return hasResource(Resources.ExtendedClients) ? true : false
    } else return true
  })

  const clientTypeTabsDefinition: Map<number, Tab> = new Map(
    filteredTabs.map((option, index) => {
      const clientTypeValue = option.value as EnumClientTypes

      return [
        index,
        {
          label: enumTranslateOption(option.value, EnumClientTypes, 'enums:EnumClientTypes'),
          id: clientTypeValue,
          hasPermission:
            hasClientOrExtendedEditReadOnlyPermission &&
            (clientTypeValue === EnumClientTypes.COMMERCIAL
              ? hasResource(Resources.Commercial)
              : true) &&
            (clientTypeValue === EnumClientTypes.WHOLESALER
              ? hasResource(Resources.Trades)
              : true) &&
            (clientTypeValue === EnumClientTypes.SHIPPER ||
            clientTypeValue === EnumClientTypes.DISTRIBUTOR
              ? hasResource(Resources.Administration)
              : true)
        }
      ]
    })
  )

  const onSelectCommodityHandler = (newCommodity: EnumCommodity): void => {
    setCommodity(newCommodity)
    storeCommodityFilterForCurrentPage(newCommodity as any)
    storeActiveCommodity(newCommodity)
  }

  const onClientTypeTabChange = (tab: number) => {
    if (currentClientTypeTab !== tab) {
      setCurrentClientTypeTab(tab)
      storeActiveTabForCurrentPage(tab)
    }
  }

  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={true}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return null
  }

  // Fetch and map invoice cycle types compatibilities
  const getInvoiceCycleCompatibilities = async () => {
    setIsLoadingCompatibilities(true)
    try {
      const response = await fetchInvoiceCycleCompatibilities()
      if (response.success && response.data) {
        setCompatibilitiesData(response.data)

        // Map to all available options
        const mappedOptions = mapCompatibilitiesToSelectOptions(response.data)
        setAllCompatibilityOptions(mappedOptions)

        // Initially set all options (no filtering)
        setSubjectTypeOptions(mappedOptions.subjectTypes)
        setInvoiceTypeOptions(mappedOptions.invoiceTypes)
        setCycleOptions(mappedOptions.cycles)
        setSubtypeOptions(mappedOptions.subtypes)
      } else {
        // On Error
        setCompatibilitiesData([])
        setAllCompatibilityOptions({
          subjectTypes: [],
          invoiceTypes: [],
          cycles: [],
          subtypes: []
        })
        setSubjectTypeOptions([])
        setInvoiceTypeOptions([])
        setCycleOptions([])
        setSubtypeOptions([])
      }
    } catch (error) {
      console.error('Error fetching invoice cycle compatibilities:', error)
      setCompatibilitiesData([])
      setAllCompatibilityOptions({
        subjectTypes: [],
        invoiceTypes: [],
        cycles: [],
        subtypes: []
      })
      setSubjectTypeOptions([])
      setInvoiceTypeOptions([])
      setCycleOptions([])
      setSubtypeOptions([])
    } finally {
      setIsLoadingCompatibilities(false)
    }
  }

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentClientTypeTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  useEffect(() => {
    getInvoiceCycleCompatibilities()
  }, [])

  const buildPlannedDate = (startDate: any, launchTime: any): string | undefined => {
    if (!startDate) return undefined
    const time = launchTime ? moment(launchTime) : moment(startDate).startOf('day')
    return moment(startDate)
      .set({ hour: time.hour(), minute: time.minute(), second: 0, millisecond: 0 })
      .format('YYYY-MM-DDTHH:mm:ss')
  }

  const handleFormFieldChange = useCallback(
    (fieldName: string, value: any) => {
      const newSelections: FormSelections = {
        ...currentFormSelections,
        [fieldName]: value ? Number(value) : undefined
      }

      // When clearing a field, also clear downstream fields (assure compatibility)
      if (value === null) {
        if (fieldName === 'subjectType') {
          // Clear all downstream fields
          newSelections.invoiceType = undefined
          newSelections.cycle = undefined
          newSelections.subtype = undefined
          updateFormDataV2((prevData: any) => ({
            ...prevData,
            invoiceType: undefined,
            cycleType: undefined,
            subtype: undefined
          }))
        } else if (fieldName === 'invoiceType') {
          // Clear cycle and subtype
          newSelections.cycle = undefined
          newSelections.subtype = undefined
          updateFormDataV2((prevData: any) => ({
            ...prevData,
            cycleType: undefined,
            subtype: undefined
          }))
        } else if (fieldName === 'cycle') {
          // Clear subtype
          newSelections.subtype = undefined
          updateFormDataV2((prevData: any) => ({
            ...prevData,
            subtype: undefined
          }))
        }
      }

      setCurrentFormSelections(newSelections)

      // Each field's options are filtered by the OTHER selected fields (not its own value).
      // This prevents the selected field from collapsing to a single option after selection.
      setSubjectTypeOptions(
        getFilteredCompatibilityOptions(compatibilitiesData, allCompatibilityOptions, {
          invoiceType: newSelections.invoiceType,
          cycle: newSelections.cycle,
          subtype: newSelections.subtype
        }).subjectTypes
      )
      setInvoiceTypeOptions(
        getFilteredCompatibilityOptions(compatibilitiesData, allCompatibilityOptions, {
          subjectType: newSelections.subjectType,
          cycle: newSelections.cycle,
          subtype: newSelections.subtype
        }).invoiceTypes
      )
      setCycleOptions(
        getFilteredCompatibilityOptions(compatibilitiesData, allCompatibilityOptions, {
          subjectType: newSelections.subjectType,
          invoiceType: newSelections.invoiceType,
          subtype: newSelections.subtype
        }).cycles
      )
      setSubtypeOptions(
        getFilteredCompatibilityOptions(compatibilitiesData, allCompatibilityOptions, {
          subjectType: newSelections.subjectType,
          invoiceType: newSelections.invoiceType,
          cycle: newSelections.cycle
        }).subtypes
      )

      // For validation: check if current selections are still compatible using the full filter
      const filteredOptions = getFilteredCompatibilityOptions(
        compatibilitiesData,
        allCompatibilityOptions,
        newSelections
      )

      // Check if current selections are still valid after filtering
      const currentSubjectType = newSelections.subjectType
      if (
        currentSubjectType &&
        !filteredOptions.subjectTypes.some(opt => opt.value === currentSubjectType)
      ) {
        updateFormDataV2((prevData: any) => ({ ...prevData, subjectType: undefined }))
        setCurrentFormSelections(prev => ({ ...prev, subjectType: undefined }))
      }

      const currentInvoiceType = newSelections.invoiceType
      if (
        currentInvoiceType &&
        !filteredOptions.invoiceTypes.some(opt => opt.value === currentInvoiceType)
      ) {
        updateFormDataV2((prevData: any) => ({ ...prevData, invoiceType: undefined }))
        setCurrentFormSelections(prev => ({ ...prev, invoiceType: undefined }))
      }

      const currentCycle = newSelections.cycle
      if (currentCycle && !filteredOptions.cycles.some(opt => opt.value === currentCycle)) {
        updateFormDataV2((prevData: any) => ({ ...prevData, cycleType: undefined }))
        setCurrentFormSelections(prev => ({ ...prev, cycle: undefined }))
      }

      const currentSubtype = newSelections.subtype
      if (currentSubtype && !filteredOptions.subtypes.some(opt => opt.value === currentSubtype)) {
        updateFormDataV2((prevData: any) => ({ ...prevData, subtype: undefined }))
        setCurrentFormSelections(prev => ({ ...prev, subtype: undefined }))
      }
    },
    [
      getFilteredCompatibilityOptions,
      currentFormSelections,
      compatibilitiesData,
      allCompatibilityOptions
    ]
  )

  // Reset form selections when create form is opened
  const resetFormSelections = useCallback(() => {
    setCurrentFormSelections({})
    setSubjectTypeOptions(allCompatibilityOptions.subjectTypes)
    setInvoiceTypeOptions(allCompatibilityOptions.invoiceTypes)
    setCycleOptions(allCompatibilityOptions.cycles)
    setSubtypeOptions(allCompatibilityOptions.subtypes)
  }, [allCompatibilityOptions])

  // Refresh component data
  const refreshData = useCallback(() => {
    // Refresh compatibilities data for the create form
    getInvoiceCycleCompatibilities()
    // Reset form selections
    resetFormSelections()

    // Trigger refresh of all child components by incrementing the trigger
    // This will cause them to re-fetch data without unmounting/remounting
    setRefreshTrigger(prev => prev + 1)
  }, [resetFormSelections])

  const handleDeleteSuccess = useCallback(() => {
    info(t('invoiceCyclesPage:delete.success', 'Ciclo de facturación eliminado exitosamente'))
  }, [info, t])

  const handleEditRef = useRef<(cycle: InvoiceCycleWithLastExecutionModel) => void>(() => {})
  const handleCreateRef = useRef<() => void>(() => {})

  const handleEdit = (cycle: InvoiceCycleWithLastExecutionModel) => {
    // Map the cycle data to form selections for cascading dropdowns
    const selections: FormSelections = {
      subjectType: cycle.subjectType,
      invoiceType: cycle.invoiceType,
      cycle: cycle.invoiceCycleType,
      subtype: cycle.invoiceCycleSubType
    }

    // Compute options cascading step by step (same as the user would do filling the form)
    // Filtering all selections at once can produce empty results for valid cycles
    // that have combinations not fully registered in compatibilities data
    const bySubjectType = getFilteredCompatibilityOptions(
      compatibilitiesData,
      allCompatibilityOptions,
      { subjectType: selections.subjectType }
    )
    const byInvoiceType = getFilteredCompatibilityOptions(
      compatibilitiesData,
      allCompatibilityOptions,
      { subjectType: selections.subjectType, invoiceType: selections.invoiceType }
    )
    const byCycle = getFilteredCompatibilityOptions(compatibilitiesData, allCompatibilityOptions, {
      subjectType: selections.subjectType,
      invoiceType: selections.invoiceType,
      cycle: selections.cycle ?? undefined
    })
    const bySubtype = getFilteredCompatibilityOptions(
      compatibilitiesData,
      allCompatibilityOptions,
      selections
    )

    const filteredOptions = {
      subjectTypes: bySubjectType.subjectTypes,
      invoiceTypes: byInvoiceType.invoiceTypes,
      cycles: byCycle.cycles,
      subtypes: bySubtype.subtypes.length > 0 ? bySubtype.subtypes : byCycle.subtypes
    }

    // Update state for subsequent interactions (cascading dropdowns)
    setCurrentFormSelections(selections)
    setSubjectTypeOptions(filteredOptions.subjectTypes)
    setInvoiceTypeOptions(filteredOptions.invoiceTypes)
    setCycleOptions(filteredOptions.cycles)
    setSubtypeOptions(filteredOptions.subtypes)
    setSelectedPeriodicityType(cycle.periodicityType ?? EnumTipoPeriodicidad.Variable)

    // Build a fresh definition with the correct options synchronously,
    // so openFormV2 doesn't receive the stale memoized createFormDefinition
    const freshDefinition = {
      ...createFormDefinition,
      sections: (createFormDefinition.sections ?? []).map(section => ({
        ...section,
        inputs: section.inputs.map(input => {
          if (input.name === 'subjectType')
            return { ...input, selectOptions: filteredOptions.subjectTypes }
          if (input.name === 'invoiceType')
            return { ...input, selectOptions: filteredOptions.invoiceTypes, readOnly: false }
          if (input.name === 'cycleType')
            return {
              ...input,
              selectOptions: filteredOptions.cycles,
              readOnly: filteredOptions.cycles.length === 0
            }
          if (input.name === 'subtype')
            return {
              ...input,
              selectOptions: filteredOptions.subtypes,
              readOnly: filteredOptions.subtypes.length === 0
            }
          return input
        })
      }))
    }

    // Open the form in edit mode with the cycle data
    openFormV2({
      definition: freshDefinition,
      title: t('invoiceCyclesPage:edit.title', 'Editar Ciclo de Facturación'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        invoiceCycleId: cycle.invoiceCycleId,
        description: cycle.description,
        subjectType: cycle.subjectType,
        invoiceType: cycle.invoiceType,
        cycleType: cycle.invoiceCycleType,
        subtype: cycle.invoiceCycleSubType,
        generateInvoice: cycle.generateInvoice ?? false,
        automatic: cycle.automatic ?? false,
        validateList: cycle.validateList ?? false,
        manualReview: cycle.manualReview ?? false,
        periodicityType: cycle.periodicityType ?? EnumPeriodicityType.Variable,
        commodity: cycle.commodity,
        startDate: cycle.plannedDate,
        launchTime: cycle.plannedDate ? cycle.plannedDate : null
      },
      okBtnLabel: t('invoiceCyclesPage:edit.save', 'Guardar cambios'),
      onClose: formData => {
        handleEditFormClose(formData)
      }
    })
  }

  // Keep ref always pointing to the latest handleEdit so AllCyclesTable's stale closure calls the fresh version
  handleEditRef.current = handleEdit
  const handleEditStable = useCallback(
    (cycle: InvoiceCycleWithLastExecutionModel) => handleEditRef.current(cycle),
    []
  )

  const handleEditFormClose = async (formData: any) => {
    if (formData) {
      // Update invoice cycle
      const request: UpdateInvoiceCycleRequest = {
        invoiceCycleId: formData.invoiceCycleId,
        description: formData.description,
        subjectType: formData.subjectType,
        invoiceType: formData.invoiceType,
        invoiceCycleType: formData.cycleType,
        invoiceCycleSubType: formData.subtype,
        generateInvoice: formData.generateInvoice || false,
        automatic: formData.automatic || false,
        validateList: formData.validateList || false,
        manualReview: formData.manualReview || false,
        periodicityType: formData.periodicityType,
        distribution: false,
        commodity: formData.commodity,
        plannedDate: buildPlannedDate(formData.startDate, formData.launchTime)
      }

      const response = await editInvoiceCycle(request)
      if (response.success) {
        info(t('invoiceCyclesPage:edit.success', 'Ciclo de facturación actualizado exitosamente'))
        // Refresh the component data to show the updated cycle
        refreshData()
      } else {
        console.error('Error updating invoice cycle:', response.error)
        error(t('globalerisLibrary:errors.generic', 'Ha ocurrido un error'))
      }
    }
    closeFormV2()
  }

  const getAvailableCycles = async () => {
    const selectOptions: SelectOption[] &
      { invoiceType: number; invoiceCycleSubtype: number | undefined }[] = []
    setIsFetchingAvailableCycles(true)
    const response = await fetchAvailableInvoiceCycles()
    if (response.success && response.data) {
      const options = response.data.map(cycle => {
        return {
          value: cycle.invoiceCycleId!,
          label: cycle.description ?? 'Ciclo sin descripción',
          invoiceType: cycle.invoiceType ?? EnumTipoFacturacion.Normal,
          invoiceCycleSubtype: cycle.invoiceCycleSubType
        }
      })
      selectOptions.push(...options)
      setAvailableCycles([...options])
    } else {
      setAvailableCycles([])
    }
    setIsFetchingAvailableCycles(false)
    return selectOptions
  }

  const handleStartExecution = () => {
    setExecutionFormInitialData(undefined)
    getAvailableCycles()
    setShowStartExecutionForm(true)
  }

  const handleStartExecutionFromRow = (cycle: InvoiceCycleWithLastExecutionModel) => {
    setAvailableCycles([
      {
        value: cycle.invoiceCycleId,
        label: cycle.description,
        invoiceType: cycle.invoiceType ?? EnumTipoFacturacion.Normal,
        invoiceCycleSubtype: cycle.invoiceCycleSubType ?? undefined
      }
    ])
    setExecutionFormInitialData({
      cycleSelect: cycle.invoiceCycleId,
      cycleName: cycle.description
    })
    setShowStartExecutionForm(true)
  }

  const handleStartExecutionFormClose = async (formData: any) => {
    setShowStartExecutionForm(false)

    if (formData) {
      const request: cfExecutionRequestModel = {
        invoiceCycleId: formData.cycleSelect,
        cfExecutionStartDate: moment(formData.startDate).format('YYYY-MM-DD'),
        cfExecutionEndDate: moment(formData.endDate).format('YYYY-MM-DD'),
        executionDate: moment().format('YYYY-MM-DD HH:mm'),
        reInvoiceType: formData.reInvoiceType,
        groupingType: formData.groupingType,
        subtype: formData?.invoiceCycleSubtype,
        cycleStatus: EnumEstadoCiclo.Iniciado
      }

      await createInvoiceCycleExecution(request).then(response => {
        if (response.success && response.data) {
          info(t('invoiceCyclesPage:startExecution.success'))

          const selectedCycle = availableCycles.find(c => c.value === formData.cycleSelect)
          const isAnnulationOrRefacturation =
            selectedCycle?.invoiceType === EnumTipoFacturacion.Anuladora ||
            selectedCycle?.invoiceType === EnumTipoFacturacion.Refacturacion

          if (isAnnulationOrRefacturation) {
            history.push(NonAuthRoutes.SettlementsAnnulation, {
              executionCycleId: response.data.cfExecutionId,
              invoiceCycleId: formData.cycleSelect,
              billingType: selectedCycle?.invoiceType,
              startDate: moment(formData.startDate).format('YYYY-MM-DD'),
              endDate: moment(formData.endDate).format('YYYY-MM-DD')
            })
          } else {
            history.push(NonAuthRoutes.invoiceCycleProcessing, {
              executionCycleId: response.data.cfExecutionId,
              invoiceCycleId: formData.cycleSelect,
              cycleName: formData.cycleName || ''
            })
          }
        } else {
          error(t('invoiceCyclesPage:startExecution.error'))
        }
      })
    }
  }

  const handleCreate = () => {
    resetFormSelections() // Reset selections when opening form
    setSelectedPeriodicityType(EnumTipoPeriodicidad.Variable)

    // Build a fresh definition with the current (fully-loaded) options synchronously,
    // so openFormV2 doesn't receive the stale memoized createFormDefinition.
    // Same approach as handleEdit — fixes subjectType appearing empty in production.
    const freshCreateDefinition = {
      ...createFormDefinition,
      sections: (createFormDefinition.sections ?? []).map(section => ({
        ...section,
        inputs: section.inputs.map(input => {
          if (input.name === 'subjectType')
            return { ...input, selectOptions: allCompatibilityOptions.subjectTypes }
          if (input.name === 'invoiceType')
            return { ...input, selectOptions: allCompatibilityOptions.invoiceTypes }
          if (input.name === 'cycleType')
            return { ...input, selectOptions: allCompatibilityOptions.cycles }
          if (input.name === 'subtype')
            return { ...input, selectOptions: allCompatibilityOptions.subtypes }
          return input
        })
      }))
    }

    openFormV2({
      definition: freshCreateDefinition,
      title: t('invoiceCyclesPage:create.title', 'Crear Ciclo de Facturación'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      inputData: {
        periodicityType: EnumPeriodicityType.Variable,
        commodity: commodity,
        subjectType: filteredTabs[currentClientTypeTab]?.value
      },
      okBtnLabel: t('invoiceCyclesPage:create.save', 'Guardar'),
      onClose: formData => {
        handleCreateFormClose(formData)
      }
    })
  }

  // Same pattern as handleEditStable — options useMemo in AllCyclesTable has deps [] so it captures
  // onCreateCycle from the first render when allCompatibilityOptions is still empty.
  handleCreateRef.current = handleCreate
  const handleCreateStable = useCallback(() => handleCreateRef.current(), [])

  const handleCreateFormClose = async (formData: any) => {
    if (formData) {
      // Create invoice cycle
      const request: CreateInvoiceCycleRequest = {
        description: formData.description,
        subjectType: formData.subjectType,
        invoiceType: formData.invoiceType,
        invoiceCycleType: formData.cycleType,
        invoiceCycleSubType: formData.subtype,
        generateInvoice: formData.generateInvoice || false,
        automatic: formData.automatic || false,
        validateList: formData.validateList || false,
        manualReview: formData.manualReview || false,
        periodicityType: formData.periodicityType,
        distribution: false,
        commodity: formData.commodity,
        plannedDate: buildPlannedDate(formData.startDate, formData.launchTime)
      }
      const response = await createInvoiceCycle(request)
      if (response.success) {
        info(t('invoiceCyclesPage:create.success', 'Ciclo de facturación creado exitosamente'))
        // Refresh the component data to show the new cycle
        refreshData()
      } else {
        console.error('Error creating invoice cycle:', response.error)
        error(t('globalerisLibrary:errors.generic', 'Ha ocurrido un error'))
      }
    }
    closeFormV2()
  }

  // Calculate next execution when periodicity, start date, or launch time changes
  const handleCalculateNextExecution = useCallback(
    async (formState: any) => {
      const periodicityType = formState.periodicityType
      const startDate = formState.startDate
      const launchTime = formState.launchTime

      // Only calculate if all required fields are present and not in Variable mode
      if (
        periodicityType &&
        periodicityType !== EnumTipoPeriodicidad.Variable &&
        startDate &&
        launchTime
      ) {
        const request: CalculateNextExecutionRequest = {
          periodicityType: periodicityType,
          plannedHour: moment(launchTime).format('HH:mm'),
          plannedDate: moment(startDate).format('YYYY-MM-DD')
        }

        try {
          const response = await calculateNextExecution(request)
          if (response.success) {
            // Update the nextExecution label with calculated date
            const nextExecutionDate = response.data?.nextExecutionDate
            const formattedDate = nextExecutionDate
              ? moment.parseZone(nextExecutionDate).format('DD/MM/YYYY HH:mm[h]')
              : '-'
            updateFormDataV2((prev: any) => ({
              ...prev,
              nextExecution: formattedDate
            }))
          }
        } catch (err) {
          console.error('Error calculating next execution:', err)
        }
      }
    },
    [calculateNextExecution, updateFormDataV2]
  )

  useEffect(() => {
    if (formPropsV2.open && formPropsV2.inputData) {
      const { periodicityType, startDate, launchTime } = formPropsV2.inputData

      if (
        periodicityType &&
        startDate &&
        launchTime &&
        periodicityType !== EnumTipoPeriodicidad.Variable
      ) {
        handleCalculateNextExecution({
          ...formPropsV2.inputData
        })
      }
    }

    if (formPropsV2.open && formPropsV2.inputData?.subjectType) {
      handleFormFieldChange('subjectType', formPropsV2.inputData.subjectType)
    }
  }, [formPropsV2.open, formPropsV2.inputData, handleCalculateNextExecution])

  // Create form definition
  const createFormDefinition = useMemo<FormDialogDefinitionProps>(() => {
    const isInvoiceTypeDisabled =
      !currentFormSelections.subjectType || invoiceTypeOptions.length === 0
    const isCycleTypeDisabled = !currentFormSelections.invoiceType || cycleOptions.length === 0
    const isSubtypeDisabled = !currentFormSelections.cycle || subjectTypeOptions.length === 0

    const definition = {
      id: 'CREATE_INVOICE_CYCLE_FORM',
      sections: [
        {
          title: t('invoiceCyclesPage:create.sectionTitle', 'Información del Ciclo'),
          inputs: [
            {
              name: 'description',
              label: t('invoiceCyclesPage:create.cycleName', 'Nombre del ciclo'),
              type: DataType.TEXT,
              required: true,
              display: true
            },
            {
              name: 'commodity',
              label: t('invoiceCyclesPage:create.commodity', 'Commodity'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
              display: true,
              readOnly: true
            },
            {
              name: 'subjectType',
              label: t('invoiceCyclesPage:create.subjectType', 'Tipo de sujeto'),
              type: DataType.SELECT,
              required: true,
              selectOptions: subjectTypeOptions,
              isLoading: isLoadingCompatibilities,
              display: true,
              readOnly: true
            },
            {
              name: 'invoiceType',
              label: t('invoiceCyclesPage:create.invoiceType', 'Tipo de facturación'),
              type: DataType.SELECT,
              required: true,
              selectOptions: invoiceTypeOptions,
              isLoading: isLoadingCompatibilities,
              display: true,
              clearable: true,
              // readOnly: isInvoiceTypeDisabled,
              // helpText:
              //   'Selecciona el tipo de sujeto para ver los tipos de facturación disponibles',
              onChange: (value: any, formState: any) => {
                setSelectedInvoiceType(value ? Number(value) : undefined)
                handleFormFieldChange('invoiceType', value)

                // Si es Anuladora o Refacturación, forza Variable y automatic false
                const isAnuladoraOrRefacturacion =
                  value === EnumTipoFacturacion.Anuladora ||
                  value === EnumTipoFacturacion.Refacturacion

                if (isAnuladoraOrRefacturacion) {
                  updateFormDataV2((prev: any) => ({
                    ...prev,
                    periodicityType: EnumTipoPeriodicidad.Variable,
                    automatic: false
                  }))
                  setSelectedPeriodicityType(EnumTipoPeriodicidad.Variable)
                }
              }
            },
            {
              name: 'cycleType',
              label: t('invoiceCyclesPage:create.cycle', 'Ciclo'),
              type: DataType.SELECT,
              required: !isCycleTypeDisabled,
              selectOptions: cycleOptions,
              isLoading: isLoadingCompatibilities,
              display: true,
              clearable: true,
              readOnly: isCycleTypeDisabled,
              helpText: 'Selecciona el tipo de facturación para ver los ciclos disponibles',
              onChange: (value: any, formState: any) => {
                handleFormFieldChange('cycle', value)
              }
            },
            {
              name: 'subtype',
              label: t('invoiceCyclesPage:create.subtype', 'Subtipo'),
              type: DataType.SELECT,
              selectOptions: subtypeOptions,
              isLoading: isLoadingCompatibilities,
              display: true,
              clearable: true,
              helpText: 'Selecciona el tipo de ciclo para ver los subtipos disponibles',
              readOnly: isSubtypeDisabled,
              onChange: (value: any, formState: any) => {
                handleFormFieldChange('subtype', value)
              }
            },
            {
              name: 'validateList',
              label: t('invoiceCyclesPage:create.validateList', 'Validar liquidaciones'),
              type: DataType.BOOLEAN,
              required: false,
              display: true,
              defaultValue: false
            },
            {
              name: 'manualReview',
              label: t('invoiceCyclesPage:create.manualReview', 'Revisión manual'),
              type: DataType.BOOLEAN,
              required: false,
              display: true,
              defaultValue: false
            },
            {
              name: 'generateInvoice',
              label: t('invoiceCyclesPage:create.generateInvoice', 'Generar factura'),
              type: DataType.BOOLEAN,
              required: false,
              display: true,
              defaultValue: false
            },
            {
              name: 'automatic',
              label: t('invoiceCyclesPage:create.scheduled', 'Planificado'),
              type: DataType.BOOLEAN,
              display: true,
              defaultValue: false,
              readOnly: true
            }
          ]
        },
        {
          title: t('invoiceCyclesPage:create.scheduledSection'),
          collapsible: true,
          inputs: [
            {
              name: 'periodicityType',
              label: t('invoiceCyclesPage:create.periodicityType', 'Periodicidad'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(
                EnumTipoPeriodicidad,
                'enums:EnumTipoPeriodicidad'
              ),
              display: true,
              defaultValue: EnumTipoPeriodicidad.Variable,
              readOnly:
                selectedInvoiceType === EnumTipoFacturacion.Anuladora ||
                selectedInvoiceType === EnumTipoFacturacion.Refacturacion,
              isLoading: isLoadingCompatibilities,
              onChange: (value: any, formState: any) => {
                setSelectedPeriodicityType(value ? Number(value) : undefined)

                const newAutomaticValue = value === EnumTipoPeriodicidad.Variable ? false : true
                const updatedFormState = {
                  ...formState,
                  automatic: newAutomaticValue
                }
                updateFormDataV2(updatedFormState)

                handleFormFieldChange('periodicityType', value)

                // Calculate next execution after periodicityType changes
                handleCalculateNextExecution(updatedFormState)
              }
            },
            {
              name: 'startDate',
              label: t('invoiceCyclesPage:create.startDate'),
              type: DataType.DATE,
              required: selectedPeriodicityType !== EnumTipoPeriodicidad.Variable,
              display: selectedPeriodicityType !== EnumTipoPeriodicidad.Variable,
              isLoading: isLoadingCompatibilities,
              onChange: (value: any, formState: any) => {
                const dateOnly = value ? moment(value).startOf('day').toDate() : value

                updateFormDataV2((prev: any) => ({
                  ...prev,
                  startDate: dateOnly
                }))

                // Calculate next execution when start date changes
                handleCalculateNextExecution({
                  ...formState,
                  startDate: dateOnly
                })
              }
            },
            {
              name: 'launchTime',
              label: t('invoiceCyclesPage:create.launchTime'),
              type: DataType.TIME,
              minutesStep: 15,
              disableOpenPicker: true,
              required: selectedPeriodicityType !== EnumTipoPeriodicidad.Variable,
              display: selectedPeriodicityType !== EnumTipoPeriodicidad.Variable,
              isLoading: isLoadingCompatibilities,
              onChange: (value: any, formState: any) => {
                if (value) {
                  const match = value.match(/T(\d{2}):(\d{2}):/)
                  if (match) {
                    const minutes = parseInt(match[2], 10)

                    if (minutes % 15 !== 0) {
                      error(t('invoiceCyclesPage:errors.launchTimeError'))
                      updateFormDataV2((prev: any) => ({
                        ...prev,
                        launchTime: undefined
                      }))
                      return
                    }
                  }
                }

                handleCalculateNextExecution(formState)
              }
            },
            {
              name: 'nextExecution',
              label: t('invoiceCyclesPage:create.nextExecution', 'Próximo lanzamiento'),
              type: DataType.TEXT,
              display: selectedPeriodicityType !== EnumTipoPeriodicidad.Variable,
              readOnly: true,
              isLoading: isLoadingCompatibilities,
              defaultValue: '-'
            }
          ]
        }
      ]
    }

    return definition
  }, [
    t,
    subjectTypeOptions,
    invoiceTypeOptions,
    cycleOptions,
    subtypeOptions,
    isLoadingCompatibilities,
    currentFormSelections,
    handleFormFieldChange,
    updateFormDataV2,
    currentFormSelections.cycle,
    currentFormSelections.invoiceType,
    currentFormSelections.subjectType
  ])

  useEffect(() => {
    updateCurrentDefinition(createFormDefinition)
  }, [createFormDefinition, updateCurrentDefinition])

  return (
    <Page>
      <PageHeader title={t('invoiceCyclesPage:title')}>
        <Button
          size="small"
          variant="text"
          color="primary"
          disabled={isFetchingAvailableCycles || isLoadingCompatibilities}
          onClick={() => {
            history.push(NonAuthRoutes.invoiceCycles)
          }}
        >
          {t('invoiceCyclesPage:buttons.back')}
        </Button>
      </PageHeader>

      <Grid container direction="column" justify="space-between" alignItems="flex-end">
        <Grid item>
          <Grid container alignItems="center" style={{ gap: 8, marginBottom: 32 }}>
            {renderCommodities()}
          </Grid>
        </Grid>
      </Grid>

      <Grid container justify="space-between" alignItems="center" style={{ marginBottom: 0 }}>
        <Grid item>
          {/* <FormControlLabel
            style={{ marginLeft: 8 }}
            control={
              <Switch
                size="small"
                checked={showAllCycles}
                onChange={handleShowAllCycles}
                name="showAllCycles"
                color="primary"
              />
            }
            label={
              <Typography variant="subtitle2" color="secondary">
                {t('invoiceCyclesPage:mainActions.showAllCycles')}
              </Typography>
            }
          /> */}
        </Grid>
        <Grid item>
          <Button size="small" variant="text" color="secondary" onClick={refreshData}>
            <Sync />
          </Button>
        </Grid>
      </Grid>

      <TabsWithPermissions
        tabs={clientTypeTabsDefinition}
        tabDefault={currentClientTypeTab}
        onTabChange={onClientTypeTabChange}
      />

      <AllCyclesTable
        refreshTrigger={refreshTrigger}
        subjectType={clientTypeTabsDefinition.get(currentClientTypeTab)?.id as number | undefined}
        activesOnly={false} //!showAllCycles}
        commodity={commodity}
        onCreateCycle={handleCreateStable}
        onEditCycle={handleEditStable}
        onStartExecution={handleStartExecutionFromRow}
        onRefreshData={refreshData}
        onDeleteSuccess={handleDeleteSuccess}
      />
      <CustomFormDialogV2 {...formPropsV2} />
      <ExecutionCycleFormDialog
        open={showStartExecutionForm}
        mode="start"
        initialData={executionFormInitialData}
        availableCycles={availableCycles}
        isFetchingAvailableCycles={isFetchingAvailableCycles}
        onClose={handleStartExecutionFormClose}
      />
    </Page>
  )
}
